import { useMemo, useState } from 'react'
import type { Recipe, ShoppingItem } from '../types'
import {
  calculatePizza,
  calculatePoolish,
  defaultPizzaOptions,
  defaultPoolishOptions,
  forceToProtein,
  proteinToForce,
  type PatonCounts,
  type PizzaOptions,
  type PoolishOptions,
  type Schedule,
  type YeastType,
  weekdays,
} from '../utils/dough'
import { shoppingItemFromIngredient } from '../utils/format'

type SpecialDoughCalculatorProps = {
  recipe: Recipe
  onAdd: (items: ShoppingItem[]) => void
}

const numberValue = (value: string, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback

export function SpecialDoughCalculator({ recipe, onAdd }: SpecialDoughCalculatorProps) {
  if (recipe.special === 'poolish') return <PoolishCalculator recipe={recipe} onAdd={onAdd} />
  return <PizzaCalculator recipe={recipe} onAdd={onAdd} />
}

function PizzaCalculator({ recipe, onAdd }: SpecialDoughCalculatorProps) {
  const [options, setOptions] = useState<PizzaOptions>(defaultPizzaOptions)
  const result = useMemo(() => calculatePizza(options), [options])
  const setPaton = (key: keyof PatonCounts, value: string) =>
    setOptions((current) => ({
      ...current,
      patons: { ...current.patons, [key]: Math.max(0, Math.min(10, numberValue(value))) },
    }))
  const setSchedule = (key: 'prepare' | 'meal', value: Partial<Schedule>) =>
    setOptions((current) => ({ ...current, [key]: { ...current[key], ...value } }))
  const ingredients = [
    calcIngredient('farine', 'farine', result.flour, 'g'),
    calcIngredient('eau', 'eau', result.water, 'g'),
    calcIngredient('sel', 'sel', result.salt, 'g'),
    calcIngredient(
      'levure',
      options.yeast === 'fraiche' ? 'levure fraîche' : 'levure sèche',
      result.yeast,
      'g',
    ),
  ].map((item) => shoppingItemFromIngredient(item, recipe.title))

  const syncForce = (force: number) =>
    setOptions((current) => ({ ...current, force, protein: forceToProtein(force) }))
  const syncProtein = (protein: number) =>
    setOptions((current) => ({ ...current, protein, force: proteinToForce(protein) }))

  return (
    <div className="special-calculator">
      <div className="calculator-intro">
        <p>Renseigne les pâtons, l’hydratation et les horaires pour calculer la pâte.</p>
      </div>
      <div className="control-grid">
        <ControlCard title="Quantités" description="Renseigne le nombre de pâtons à préparer.">
          <PatonInputs value={options.patons} onChange={setPaton} />
        </ControlCard>
        <ControlCard
          title="Hydratation"
          description="Plus elle est élevée, plus la manipulation est technique."
        >
          <RangeField
            label="Hydratation"
            value={options.hydration}
            min={45}
            max={75}
            suffix="%"
            onChange={(value) => setOptions((current) => ({ ...current, hydration: value }))}
          />
        </ControlCard>
        <ControlCard
          title="Quand manger ?"
          description="Ces horaires servent à calculer la maturation et le moment du boulage."
        >
          <ScheduleFields
            label="Préparation"
            value={options.prepare}
            onChange={(value) => setSchedule('prepare', value)}
          />
          <ScheduleFields
            label="Repas"
            value={options.meal}
            onChange={(value) => setSchedule('meal', value)}
          />
        </ControlCard>
        <ControlCard
          title="Température de levée"
          description="Température moyenne pendant le repos de la pâte."
        >
          <RangeField
            label="Température"
            value={options.temperature}
            min={17}
            max={27}
            suffix="°C"
            onChange={(value) => setOptions((current) => ({ ...current, temperature: value }))}
          />
        </ControlCard>
        <ControlCard title="Type de farine" description="La force W et le taux de protéines sont liés.">
          <RangeField
            label="Force"
            value={options.force}
            min={170}
            max={450}
            suffix="W"
            onChange={syncForce}
          />
          <RangeField
            label="Protéines"
            value={options.protein}
            min={10.7}
            max={15.9}
            step={0.1}
            suffix="g / 100 g"
            onChange={syncProtein}
          />
        </ControlCard>
        <ControlCard title="Levure" description="Choisis la forme utilisée dans la pâte.">
          <div className="radio-row">
            <label>
              <input
                checked={options.yeast === 'seche'}
                onChange={() => setOptions((current) => ({ ...current, yeast: 'seche' }))}
                type="radio"
              />{' '}
              Sèche
            </label>
            <label>
              <input
                checked={options.yeast === 'fraiche'}
                onChange={() => setOptions((current) => ({ ...current, yeast: 'fraiche' }))}
                type="radio"
              />{' '}
              Fraîche
            </label>
          </div>
          <p className="calculated-small">
            Maturation indicative : {result.maturationMin} à {result.maturationMax} h
          </p>
        </ControlCard>
      </div>

      <DoughResultCard
        title="Préparation de la pâte"
        result={result}
        ingredients={ingredients}
        onAdd={() => onAdd(ingredients)}
        pizza
        options={options}
      />

      <section className="calculator-section pitcher-section">
        <div>
          <h2>Sans balance de précision ?</h2>
          <p>Dilue ta levure dans un grand volume d’eau puis utilise la quantité d’eau levurée indiquée.</p>
        </div>
        <div className="inline-fields">
          <NumberField
            label="Eau du pichet"
            value={options.pitcherWater}
            suffix="mL"
            min={300}
            max={2000}
            onChange={(value) => setOptions((current) => ({ ...current, pitcherWater: value }))}
          />
          <NumberField
            label="Levure du pichet"
            value={options.pitcherYeast}
            suffix="g"
            min={1}
            max={10}
            step={0.1}
            onChange={(value) => setOptions((current) => ({ ...current, pitcherYeast: value }))}
          />
        </div>
        <div className="dilution-result">
          <strong>{result.waterYeasted} g</strong> d’eau levurée <span>+</span>{' '}
          <strong>{result.waterClear} g</strong> d’eau claire
        </div>
      </section>
    </div>
  )
}

function PoolishCalculator({ recipe, onAdd }: SpecialDoughCalculatorProps) {
  const [options, setOptions] = useState<PoolishOptions>(defaultPoolishOptions)
  const result = useMemo(() => calculatePoolish(options), [options])
  const setPaton = (key: keyof PatonCounts, value: string) =>
    setOptions((current) => ({
      ...current,
      patons: { ...current.patons, [key]: Math.max(0, Math.min(10, numberValue(value))) },
    }))
  const ingredients = [
    calcIngredient('poolish-farine', 'farine pour la poolish', result.poolishFlour, 'g', 'farine', 'farine'),
    calcIngredient('poolish-eau', 'eau pour la poolish', result.poolishWater, 'g', 'eau', 'eau'),
    calcIngredient('poolish-levure', 'levure pour la poolish', result.poolishYeast, 'g'),
    calcIngredient('poolish-miel', 'miel pour la poolish', result.poolishHoney, 'g'),
    calcIngredient('farine-restante', 'farine après repos', result.remainingFlour, 'g', 'farine', 'farine'),
    calcIngredient('eau-restante', 'eau après repos', result.remainingWater, 'g', 'eau', 'eau'),
    calcIngredient('sel', 'sel', result.salt, 'g'),
  ].map((item) => shoppingItemFromIngredient(item, recipe.title))

  return (
    <div className="special-calculator">
      <div className="calculator-intro">
        <p>Calcule une pâte avec une poolish à 100 % d’hydratation.</p>
      </div>
      <div className="control-grid three-columns">
        <ControlCard title="Quantités" description="Renseigne le nombre de pâtons à préparer.">
          <PatonInputs value={options.patons} onChange={setPaton} />
        </ControlCard>
        <ControlCard
          title="Hydratation"
          description="Plus elle est élevée, plus la manipulation est technique."
        >
          <RangeField
            label="Hydratation"
            value={options.hydration}
            min={45}
            max={75}
            suffix="%"
            onChange={(value) => setOptions((current) => ({ ...current, hydration: value }))}
          />
        </ControlCard>
        <ControlCard title="% Poolish" description="Pourcentage du poids total de pâte mis en poolish.">
          <RangeField
            label="Poolish"
            value={options.poolishPercentage}
            min={40}
            max={80}
            suffix="%"
            onChange={(value) => setOptions((current) => ({ ...current, poolishPercentage: value }))}
          />
        </ControlCard>
      </div>
      <PoolishResultCard result={result} ingredients={ingredients} onAdd={() => onAdd(ingredients)} />
    </div>
  )
}

function calcIngredient(
  id: string,
  name: string,
  amount: number,
  unit: string,
  shoppingKey = name,
  shoppingLabel = name,
) {
  return { id, name, amount, unit, shoppingKey, shoppingLabel }
}

function ControlCard({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <section className="control-card">
      <div className="control-card-heading">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {children}
    </section>
  )
}

function PatonInputs({
  value,
  onChange,
}: {
  value: PatonCounts
  onChange: (key: keyof PatonCounts, value: string) => void
}) {
  const fields: Array<[keyof PatonCounts, string, number]> = [
    ['mega', 'Énorme', 330],
    ['gros', 'Gros', 300],
    ['classique', 'Classique', 250],
    ['mini', 'Mini', 100],
  ]
  return (
    <div className="paton-inputs">
      {fields.map(([key, label, weight]) => (
        <label key={key}>
          <span>
            {label} <small>({weight} g)</small>
          </span>
          <input
            min="0"
            max="10"
            onChange={(event) => onChange(key, event.target.value)}
            type="number"
            value={value[key]}
          />
        </label>
      ))}
    </div>
  )
}

function RangeField({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  suffix: string
  onChange: (value: number) => void
}) {
  return (
    <label className="range-field">
      <span>
        <strong>{label}</strong>
        <output>
          {value.toLocaleString('fr-FR')} {suffix}
        </output>
      </span>
      <input
        max={max}
        min={min}
        onChange={(event) => onChange(numberValue(event.target.value, min))}
        step={step}
        type="range"
        value={value}
      />
    </label>
  )
}

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  suffix: string
  onChange: (value: number) => void
}) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <div>
        <input
          max={max}
          min={min}
          onChange={(event) => onChange(numberValue(event.target.value, min))}
          step={step}
          type="number"
          value={value}
        />
        <small>{suffix}</small>
      </div>
    </label>
  )
}

function ScheduleFields({
  label,
  value,
  onChange,
}: {
  label: string
  value: Schedule
  onChange: (value: Partial<Schedule>) => void
}) {
  return (
    <div className="schedule-fields">
      <span>{label}</span>
      <div>
        <select
          aria-label={`${label} : jour`}
          onChange={(event) => onChange({ day: event.target.value })}
          value={value.day}
        >
          {weekdays.map((day) => (
            <option key={day}>{day}</option>
          ))}
        </select>
        <input
          aria-label={`${label} : heure`}
          max="23"
          min="0"
          onChange={(event) => onChange({ hour: numberValue(event.target.value) })}
          type="number"
          value={value.hour}
        />
        <b>h</b>
        <input
          aria-label={`${label} : minutes`}
          max="59"
          min="0"
          onChange={(event) => onChange({ minute: numberValue(event.target.value) })}
          type="number"
          value={value.minute}
        />
        <b>m</b>
      </div>
    </div>
  )
}

function PoolishResultCard({
  result,
  ingredients,
  onAdd,
}: {
  result: ReturnType<typeof calculatePoolish>
  ingredients: ShoppingItem[]
  onAdd: () => void
}) {
  const amount = (value: number) => value.toLocaleString('fr-FR')
  return (
    <section className="calculator-section result-card">
      <div className="result-heading">
        <h2>Préparation de la pâte (avec poolish)</h2>
        <button className="button" onClick={onAdd} type="button">
          Ajouter à la liste de courses
        </button>
      </div>
      <div className="poolish-result-layout">
        <div className="poolish-breakdown">
          <section className="poolish-phase">
            <h3>
              Poolish <small>(base à 100 % d’hydratation · {amount(result.poolishWeight)} g)</small>
            </h3>
            <ul className="result-ingredients">
              <li>
                <strong>{amount(result.poolishWater)} g</strong> d’eau
              </li>
              <li>
                <strong>{amount(result.poolishFlour)} g</strong> de farine
              </li>
              <li>
                <strong>{amount(result.poolishYeast)} g</strong> de levure
              </li>
              <li>
                <strong>{amount(result.poolishHoney)} g</strong> de miel
              </li>
            </ul>
          </section>
          <section className="poolish-phase">
            <h3>
              Après le repos <small>(16 à 24 h)</small>
            </h3>
            <p className="phase-lead">Compléter avec :</p>
            <ul className="result-ingredients">
              <li>
                <strong>{amount(result.remainingWater)} g</strong> d’eau
              </li>
              <li>
                <strong>{amount(result.remainingFlour)} g</strong> de farine
              </li>
              <li>
                <strong>{amount(result.salt)} g</strong> de sel
              </li>
            </ul>
          </section>
          <div className="poolish-totals">
            <span>Totaux</span>
            <strong>{amount(result.flour)} g de farine</strong>
            <strong>{amount(result.water)} g d’eau froide</strong>
          </div>
        </div>
        <aside className="result-aside">
          <p>Le pourcentage est calculé sur le poids total de pâte.</p>
          <p>
            La levure et le miel sont entièrement incorporés dans la poolish : aucune levure n’est ajoutée
            après le repos.
          </p>
          <p>Le sel n’est ajouté qu’au moment de compléter, dans la pâte finale.</p>
        </aside>
      </div>
    </section>
  )
}

function DoughResultCard({
  title,
  result,
  ingredients,
  onAdd,
  pizza,
  options,
}: {
  title: string
  result: ReturnType<typeof calculatePizza>
  ingredients: ShoppingItem[]
  onAdd: () => void
  pizza?: boolean
  options?: PizzaOptions
}) {
  return (
    <section className="calculator-section result-card">
      <div className="result-heading">
        <h2>{title}</h2>
        <button className="button" onClick={onAdd} type="button">
          Ajouter à la liste de courses
        </button>
      </div>
      <div className="result-layout">
        <div>
          <p className="result-total">
            {result.totalWeight} g <span>de pâte</span>
          </p>
          <ul className="result-ingredients">
            {ingredients.map((ingredient, index) => (
              <li key={`${pizza ? 'pizza' : 'poolish'}-result-ingredient-${ingredient.id}-${index}`}>
                <strong>
                  {ingredient.amount?.toLocaleString('fr-FR')} {ingredient.unit}
                </strong>{' '}
                {ingredient.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="result-aside">
          {pizza && options && (
            <>
              <p>
                Température de l’eau : <strong>environ {result.waterTemperature} °C</strong>
              </p>
              <p>
                Farine conseillée : <strong>W{result.recommendedFlour}</strong> (
                {result.recommendedProtein.toLocaleString('fr-FR')} g de protéines / 100 g)
              </p>
              <p>
                Préparation conseillée :{' '}
                <strong>
                  {result.recommendedPrepare.day} à {result.recommendedPrepare.hour}h
                  {String(result.recommendedPrepare.minute).padStart(2, '0')}
                </strong>
              </p>
              <p>
                Boulage conseillé :{' '}
                <strong>
                  {result.recommendedBalling.day} à {result.recommendedBalling.hour}h
                  {String(result.recommendedBalling.minute).padStart(2, '0')}
                </strong>
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
